/**
 * DecisionChainPanel — F1: łańcuch decyzyjny per inwestycja (2026-05-06).
 *
 * Pokazuje 6 ról: inwestor → architekt → projektant oświetlenia (KEY) → GW → wykonawca →
 * osoba kontaktowa zamówienia. Auto-research z GUNB + SWZ NLP + manual edit.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import {
  Users, Sparkles, Edit2, Save, X, BadgeCheck, Mail, Phone, ExternalLink,
  Crown, RefreshCw, ArrowRight,
} from 'lucide-react';
import {
  getDecisionChain, triggerChainResearch, updateDecisionChain,
  sphinxEnrichChainRole,
} from '../../lib/api';
import { useNavigate } from 'react-router-dom';

// Color per role
const ROLE_COLORS = {
  investor: 'border-amber-500/40 bg-amber-500/5',
  architect: 'border-blue-500/40 bg-blue-500/5',
  interior_designer: 'border-fuchsia-500/60 bg-fuchsia-500/10',  // KEY dla biurowców/hoteli/retail
  lighting_designer: 'border-emerald-500/60 bg-emerald-500/10',  // KEY dla LED
  decision_maker: 'border-rose-500/60 bg-rose-500/10',  // KEY — finalny decydent
  general_contractor: 'border-purple-500/40 bg-purple-500/5',
  electrical_contractor: 'border-cyan-500/40 bg-cyan-500/5',
  procurement_contact: 'border-zinc-500/40 bg-zinc-500/5',
};

const KEY_ROLES = new Set(['interior_designer', 'lighting_designer', 'decision_maker']);

const SOURCE_LABEL = {
  manual: { label: 'manualne', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  gunb: { label: 'GUNB pozwolenie', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  swz_nlp: { label: 'SWZ NLP', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  opportunity: { label: 'BZP/TED', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  krs: { label: 'KRS', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
};

function ActorCard({ roleKey, roleLabel, roleDesc, actor, onEdit, isKey, altCandidates, onAcceptCandidate, onSphinxEnrich, sphinxEnriching }) {
  const navigate = useNavigate();
  const filled = !!actor.name;
  const colorClass = ROLE_COLORS[roleKey] || 'border-muted bg-muted/5';
  const sourceTag = actor.source?.split(':')[0];
  const srcMeta = SOURCE_LABEL[sourceTag];
  // Cross-link do architects_tracked (gdy auto-import z GUNB)
  const linkedArchitectId = actor.architects_tracked_id;
  // Etap 2a: alternative candidates dla decision_maker (SPHINX board members)
  const hasAlternatives = altCandidates && altCandidates.length > 1;
  // Etap 5: Adam Pattern Learning — gdy source = 'adam_pattern_learning'
  const isAdamPattern = actor.source === 'adam_pattern_learning';
  const patternObservations = actor.pattern_observations || 0;

  return (
    <Card className={`border-2 ${colorClass}`}>
      <CardContent className="pt-3 pb-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isKey && <Crown className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                {roleLabel}
              </span>
              {actor.verified && (
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" title="Zweryfikowany manualnie" />
              )}
              {srcMeta && filled && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${srcMeta.cls}`}>
                  {srcMeta.label}
                </span>
              )}
              {/* Etap 5: Adam Pattern Learning badge */}
              {isAdamPattern && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded border bg-purple-500/15 text-purple-300 border-purple-500/40 gap-0.5 inline-flex items-center"
                  title={`Adam pamięta tę osobę z ${patternObservations} wcześniejszych zaakceptowanych projektów`}
                >
                  🧠 Adam pamięta z {patternObservations}
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground/60 mt-0.5">{roleDesc}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            {/* T33 — SPHINX enrich per-role (gdy filled + brakuje email/phone) */}
            {actor && actor.name && (!actor.email || !actor.phone) && onSphinxEnrich && (
              <Button
                size="sm" variant="ghost" className="h-7 px-2"
                onClick={() => onSphinxEnrich(roleKey)}
                disabled={sphinxEnriching === roleKey}
                title="Wzbogać kontakty (email/phone/LinkedIn) przez SPHINX OSINT + KRS"
              >
                {sphinxEnriching === roleKey
                  ? <span className="text-xs">⏳</span>
                  : <span className="text-amber-400">✨</span>}
              </Button>
            )}
            <Button
              size="sm" variant="ghost" className="h-7 px-2"
              onClick={() => onEdit(roleKey)}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {filled ? (
          <div className="space-y-1">
            <div className="text-sm font-medium">{actor.name}</div>
            {/* Uczciwość (zgł. 083af4d0): dane z pozwolenia GUNB to zapis urzędowy,
                może nie odpowiadać realnemu wykonawcy/projektantowi. Ostrzegamy gdy niezweryfikowane. */}
            {sourceTag === 'gunb' && !actor.verified && (
              <div className="text-[10px] text-amber-300/80 flex items-start gap-1">
                <span>⚠</span>
                <span>Dane z pozwolenia GUNB — zapis urzędowy, może różnić się od faktycznego projektanta/wykonawcy. Zweryfikuj przed kontaktem.</span>
              </div>
            )}
            {actor.company && actor.company !== actor.name && (
              <div className="text-xs text-muted-foreground">{actor.company}</div>
            )}
            {actor.license && (
              <div className="text-xs text-muted-foreground">Uprawnienia: {actor.license}</div>
            )}
            {(actor.email || actor.phone || actor.linkedin) ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {actor.email && (
                  <a
                    href={`mailto:${actor.email}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Mail className="w-3 h-3" /> {actor.email}
                  </a>
                )}
                {actor.phone && (
                  <a
                    href={`tel:${actor.phone}`}
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <Phone className="w-3 h-3" /> {actor.phone}
                  </a>
                )}
                {actor.linkedin && (
                  <a
                    href={actor.linkedin}
                    target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" /> LinkedIn
                  </a>
                )}
              </div>
            ) : (
              // Bug report b: architect present but no contact — show muted note (do not fabricate data)
              roleKey === 'architect' && (
                <div className="text-[11px] text-muted-foreground/70 italic pt-1">
                  kontakt do uzupełnienia
                </div>
              )
            )}
            {actor.notes && (
              <div className="text-xs text-muted-foreground/80 italic pt-1">{actor.notes}</div>
            )}
            {linkedArchitectId && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-[11px] text-primary gap-1 mt-1"
                onClick={() => navigate(`/architects?license=${encodeURIComponent(actor.license || '')}`)}
              >
                Zobacz profil w bazie architektów
                <ArrowRight className="w-2.5 h-2.5" />
              </Button>
            )}
          </div>
        ) : (
          // Bug report b (Iza): empty roles showed generic "brak danych" with no explanation.
          // Show role-specific honest empty-state text instead.
          <div className="text-xs text-muted-foreground italic py-2">
            {roleKey === 'investor' ? (
              <span>Inwestor nieujawniony w rejestrze GUNB (często osoba prywatna — RODO).</span>
            ) : roleKey === 'general_contractor' || roleKey === 'electrical_contractor' ? (
              <span>Wykonawca nieznany na etapie pozwolenia — wybierany dopiero po ogłoszeniu przetargu.</span>
            ) : (
              <span>— brak danych — kliknij ✎ żeby dodać</span>
            )}
          </div>
        )}

        {/* Etap 2a: Alternative candidates (SPHINX/Serper board members) */}
        {hasAlternatives && (
          <div className="pt-2 mt-2 border-t border-border/50">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center justify-between">
              <span>📋 Kandydaci z {actor.bip_url || actor.source?.includes('bip') ? 'BIP' : 'KRS'} ({altCandidates.length})</span>
              {actor.bip_url && (
                <a href={actor.bip_url} target="_blank" rel="noreferrer" className="text-primary text-[10px] hover:underline">
                  🏛 BIP →
                </a>
              )}
            </div>
            <div className="space-y-1">
              {altCandidates.slice(0, 5).map((c, i) => {
                const isCurrent = c.name === actor.name;
                return (
                  <div
                    key={i}
                    className={`flex items-center justify-between gap-2 text-[11px] p-1.5 rounded ${
                      isCurrent ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {c.name}
                        {c.linkedin_url && (
                          <a
                            href={c.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="Profil LinkedIn"
                            className="text-blue-400 hover:text-blue-300 inline-flex"
                          >
                            <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </div>
                      <div className="text-muted-foreground text-[10px] flex gap-1.5 items-center">
                        <span>{c.position_label}</span>
                        <span>·</span>
                        <span className={
                          c.score >= 0.8 ? 'text-emerald-400' :
                          c.score >= 0.6 ? 'text-amber-400' : 'text-zinc-400'
                        }>
                          {Math.round(c.score * 100)}%
                        </span>
                        {(c.source || '').includes('bip') && (
                          <span className="text-purple-300">· BIP</span>
                        )}
                      </div>
                    </div>
                    {/* Action buttons: ✓ accept / ✗ reject / 📞 outreach */}
                    {!isCurrent && (
                      <div className="flex gap-1 shrink-0">
                        {onAcceptCandidate && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-emerald-400 hover:bg-emerald-500/10"
                            title="Zaakceptuj jako primary"
                            onClick={(e) => { e.stopPropagation(); onAcceptCandidate(c); }}
                          >
                            ✓
                          </Button>
                        )}
                        {c.linkedin_url && (
                          <a
                            href={c.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title="LinkedIn outreach"
                            className="h-6 w-6 p-0 flex items-center justify-center text-blue-400 hover:bg-blue-500/10 rounded text-xs"
                          >
                            📞
                          </a>
                        )}
                      </div>
                    )}
                    {isCurrent && <span className="text-emerald-400 text-[10px] shrink-0">✓ wybrany</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EditDialog({ roleKey, roleLabel, actor, onSave, onCancel }) {
  const [form, setForm] = useState({ ...actor });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Card className="border-primary/40">
      <CardContent className="pt-4 pb-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">Edycja: {roleLabel}</div>
          <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Imię i nazwisko / Nazwa firmy"
                 value={form.name || ''} onChange={e => update('name', e.target.value)} />
          <Input placeholder="Firma / biuro projektowe"
                 value={form.company || ''} onChange={e => update('company', e.target.value)} />
          <Input placeholder="NIP" value={form.nip || ''} onChange={e => update('nip', e.target.value)} />
          <Input placeholder="Uprawnienia (np. PR/12345/2020)" value={form.license || ''}
                 onChange={e => update('license', e.target.value)} />
          <Input type="email" placeholder="email" value={form.email || ''}
                 onChange={e => update('email', e.target.value)} />
          <Input placeholder="telefon" value={form.phone || ''}
                 onChange={e => update('phone', e.target.value)} />
          <Input placeholder="LinkedIn URL" value={form.linkedin || ''}
                 onChange={e => update('linkedin', e.target.value)}
                 className="col-span-2" />
        </div>
        <Textarea placeholder="Notatki (np. relacja, historia kontaktów)"
                  value={form.notes || ''} onChange={e => update('notes', e.target.value)}
                  className="min-h-[60px]" />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSave(form)}>
            <Save className="w-3.5 h-3.5 mr-1" /> Zapisz
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>Anuluj</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DecisionChainPanel({ tenderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role key
  const [researching, setResearching] = useState(false);
  const [sphinxEnriching, setSphinxEnriching] = useState(null); // role key being enriched

  // T33 — per-role SPHINX enrich (response na zgł. Miro 638c28af "dane kontaktowe")
  const handleSphinxEnrich = async (roleKey) => {
    setSphinxEnriching(roleKey);
    try {
      toast.info(`SPHINX wzbogaca ${roleKey} (osint + KRS)... ~90s`);
      const r = await sphinxEnrichChainRole(tenderId, roleKey, 'full');
      if (r.auto_filled && r.auto_filled.length > 0) {
        toast.success(`✨ Wzbogacono: ${r.auto_filled.join(', ')}` +
          (r.risk_signals_count > 0 ? ` · ⚠ ${r.risk_signals_count} risk signals` : ''));
      } else if (r.risk_signals_count > 0) {
        toast.warning(`Brak nowych kontaktów ale wykryto ${r.risk_signals_count} risk signals — zapisane w notes`);
      } else {
        toast.info('SPHINX nie znalazł nowych danych dla tej osoby/firmy');
      }
      load();
    } catch (e) {
      toast.error('SPHINX enrich failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSphinxEnriching(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getDecisionChain(tenderId);
      setData(d);
    } catch (e) {
      toast.error('Błąd ładowania łańcucha: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const handleResearch = async () => {
    setResearching(true);
    try {
      toast.info('Adam szuka łańcucha decyzyjnego (GUNB + SWZ NLP + KRS)...');
      const r = await triggerChainResearch(tenderId);
      const sources = r.research_sources_used || [];
      if (sources.length === 0) {
        toast.warning('Nic nie znaleziono — uzupełnij ręcznie. (GUNB nie ma rekordów dla tej lokalizacji?)');
      } else {
        toast.success(`Auto-research OK — źródła: ${sources.join(', ')}`);
      }
      await load();
    } catch (e) {
      toast.error('Research failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setResearching(false);
    }
  };

  const handleSaveActor = async (form) => {
    try {
      const body = { [editing]: form };
      await updateDecisionChain(tenderId, body);
      toast.success(`Zapisano: ${editing}`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error('Zapis nie powiódł się: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Etap 2a (2026-05-15): accept alternative candidate z SPHINX/Serper discovery
  // Przepisuje wybranego candidate'a do actor + verified=true
  const handleAcceptCandidate = async (roleKey, candidate) => {
    try {
      const actorForm = {
        name: candidate.name,
        position_label: candidate.position_label,
        notes: `${candidate.position_label} (KRS confidence ${Math.round(candidate.score * 100)}%, source: ${candidate.source})`,
        source: candidate.source,
        verified: true,
      };
      await updateDecisionChain(tenderId, { [roleKey]: actorForm });
      toast.success(`Zaakceptowano: ${candidate.name} jako ${candidate.position_label}`);
      await load();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="space-y-2"><Skeleton className="h-32" /><Skeleton className="h-32" /></div>;
  if (!data) return null;

  const { chain, summary, roles_meta } = data;

  return (
    <div className="space-y-3">
      {/* Header */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Łańcuch decyzyjny
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Inwestor → architekt → architekt wnętrz → projektant oświetlenia → decision maker → GW → elektryk.
              </p>
              {/* Completeness bar */}
              <div className="flex items-center gap-2 mt-2">
                <div className="flex-1 max-w-[300px] h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      (chain.completeness_pct || 0) >= 60 ? 'bg-emerald-500'
                      : (chain.completeness_pct || 0) >= 30 ? 'bg-amber-500'
                      : 'bg-red-500/60'
                    }`}
                    style={{ width: `${chain.completeness_pct || 0}%` }}
                  />
                </div>
                <span className="text-xs font-medium">
                  {chain.filled_roles_count || 0}/{chain.total_roles_count || 8}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({(chain.completeness_pct || 0).toFixed(0)}%)
                </span>
                {chain.ready_for_outreach ? (
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px]">
                    🟢 Gotowe do outreachu
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">
                    🟡 Wymaga researchu
                  </Badge>
                )}
              </div>
              {chain.last_research_at && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  Ostatnie auto-research: {new Date(chain.last_research_at).toLocaleString('pl-PL')}
                </p>
              )}
            </div>
            <Button onClick={handleResearch} disabled={researching} size="sm">
              {researching
                ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Auto-research
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Edit dialog */}
      {editing && (
        <EditDialog
          roleKey={editing}
          roleLabel={roles_meta.find(r => r.key === editing)?.label || editing}
          actor={chain[editing] || {}}
          onSave={handleSaveActor}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* 8 actor cards in 2-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {roles_meta.map(({ key, label, description }) => (
          <ActorCard
            key={key}
            roleKey={key}
            roleLabel={label}
            roleDesc={description}
            actor={chain[key] || {}}
            isKey={KEY_ROLES.has(key)}
            onEdit={() => setEditing(key)}
            // NEW Etap 2a: dla decision_maker pokaz alternative candidates z SPHINX/Serper
            altCandidates={key === 'decision_maker' ? (chain.decision_maker_candidates || []) : null}
            onAcceptCandidate={(candidate) => handleAcceptCandidate(key, candidate)}
            // T33: per-role SPHINX enrich
            onSphinxEnrich={handleSphinxEnrich}
            sphinxEnriching={sphinxEnriching}
          />
        ))}
      </div>
    </div>
  );
}
